// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CollapsibleCard from "components/CollapsibleCard";
import OcrolusDataTable from "./OcrolusDataTable";
import Chip from "@mui/material/Chip";
import Loader from "components/Loader/Basic";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import { get } from "requests";

interface Props {
  dealApplicationId: number;
}

const OcrolusDetectSignalsCard: React.FC<Props> = ({ dealApplicationId }) => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any[]>([]);

  useEffect(() => {
    if (!dealApplicationId) {
      setLoading(false);
      return;
    }

    const fetchSignals = async () => {
      try {
        setLoading(true);
        const res = await get(
          `/integration/ocrolous/book/${dealApplicationId}/detect-signals`
        );

        if (res?.status === "success") {
          setDocuments(res?.data?.documents || []);
        } else {
          setDocuments([]);
        }
      } catch (e) {
        console.error("Failed to load detect signals", e);
        setDocuments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSignals();
  }, [dealApplicationId]);

  const rows = useMemo(() => {
    return documents.map((doc, index) => ({
      _rowId: doc.uuid || `doc-${index}`,
      name: doc.name || "-",
      authenticityScore: doc.authenticityScore,
      signals: doc.signals || [],
      dashboardUrl: doc.dashboardUrl,
    }));
  }, [documents]);

  const hasAnySignals = useMemo(
    () => rows.some((r) => r.signals.length > 0),
    [rows]
  );

  const columns = [
    {
      Header: "DOCUMENT",
      accessor: "name",
      align: "left",
      Cell: ({ row }: any) => (
        <MDBox display="flex" alignItems="center" gap={1}>
          <DocumentScannerIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <MDTypography variant="body2" fontWeight="medium">
            {row?.name || "-"}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "AUTHENTICITY SCORE",
      accessor: "authenticityScore",
      align: "left",
      Cell: ({ row }: any) => {
        const score = row?.authenticityScore;
        if (score == null) return "-";

        const isHighScore = score >= 70;
        return (
          <MDBox display="flex" alignItems="center" gap={1}>
            {isHighScore ? (
              <VerifiedIcon sx={{ fontSize: 18, color: "success.main" }} />
            ) : (
              <WarningAmberIcon sx={{ fontSize: 18, color: "warning.main" }} />
            )}
            <Chip
              size="small"
              label={`${score}/100`}
              color={isHighScore ? "success" : "warning"}
              variant={isHighScore ? "filled" : "outlined"}
              sx={{
                height: 24,
                fontSize: "11px",
                fontWeight: 600,
                minWidth: 70,
              }}
            />
          </MDBox>
        );
      },
    },
    {
      Header: "SIGNALS",
      accessor: "signals",
      align: "left",
      Cell: ({ row }: any) => {
        const signals = row?.signals || [];

        if (!signals.length) {
          return (
            <MDBox display="flex" alignItems="center" gap={0.5}>
              <VerifiedIcon sx={{ fontSize: 16, color: "success.main" }} />
              <MDTypography variant="caption" color="success" fontWeight="medium">
                Verified
              </MDTypography>
            </MDBox>
          );
        }

        return (
          <MDBox display="flex" flexWrap="wrap" gap={0.75}>
            {signals.map((s: any) => (
              <Chip
                key={s.identifier}
                size="small"
                label={s.displayName}
                color="warning"
                icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 24,
                  fontSize: "11px",
                  fontWeight: 600,
                  border: "1px solid",
                  borderColor: "warning.light",
                }}
              />
            ))}
          </MDBox>
        );
      },
    },
  ];

  return (
    <CollapsibleCard header="Document Verification Signals">
      <MDBox p={3}>
        {loading ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <Loader />
          </MDBox>
        ) : documents.length === 0 ? (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={2}
            textAlign="center"
          >
            <VerifiedIcon
              sx={{
                fontSize: 64,
                color: "success.light",
                mb: 2,
                opacity: 0.8,
              }}
            />
            <MDTypography variant="h6" color="success" fontWeight="medium" gutterBottom>
              All Documents Verified
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" maxWidth={400}>
              No unusual activity detected in this book.
            </MDTypography>
          </MDBox>
        ) : !hasAnySignals ? (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={2}
            textAlign="center"
          >
            <VerifiedIcon
              sx={{
                fontSize: 64,
                color: "success.light",
                mb: 2,
                opacity: 0.8,
              }}
            />
            <MDTypography variant="h6" color="success" fontWeight="medium" gutterBottom>
              No Issues Found
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" maxWidth={400}>
              All documents have been verified with no suspicious signals detected.
            </MDTypography>
          </MDBox>
        ) : (
          <OcrolusDataTable columns={columns} data={rows} rowKey="_rowId" />
        )}
      </MDBox>
    </CollapsibleCard>
  );
};

export default OcrolusDetectSignalsCard;